/**
 * KOL-108: mehrere Dateien in einer Nachricht.
 *
 * Der Nachrichtentext trägt beliebig viele `[efile:…]`-Token — der Parser liest
 * sie seit KOL-90 global. Was fehlte, war der Weg dorthin: der Composer nahm
 * genau eine Datei und verwarf den Rest kommentarlos.
 *
 * Hier liegt nur die Entscheidungslogik: was wird angenommen, was abgelehnt,
 * und wie entsteht daraus ein Nachrichtentext. Das Hochladen bleibt im
 * Composer, damit diese Regeln ohne Browser prüfbar sind.
 */

/** Höchstzahl Dateien je Nachricht. */
export const MAX_FILES = 10;

/**
 * Obergrenze über alle Dateien einer Nachricht. Der Server begrenzt jede
 * einzelne Datei auf 50 MB (file.service.ts) — ohne Summengrenze landen zehn
 * davon in einem Kanal, und jeder Empfänger entschlüsselt sie einzeln.
 */
export const MAX_TOTAL_BYTES = 100 * 1024 * 1024;

export type PendingFile = {
  file: File;
  /** Objekt-URL für Bildvorschau; null bei allem anderen. */
  preview: string | null;
};

export type Abgelehnt = {
  name: string;
  grund: "zu_viele" | "zu_gross";
};

/**
 * Nimmt neue Dateien zu den bereits gewählten hinzu und sagt, was nicht passt.
 *
 * Angenommen wird, was unter beide Grenzen passt; der Rest wird benannt
 * zurückgegeben, damit der Composer es zeigen kann. Stilles Verschlucken ist
 * genau der Fehler, den dieses Ticket behebt — es darf hier nicht zurückkommen.
 */
export function dateienAnnehmen(
  vorhanden: readonly PendingFile[],
  neue: readonly File[],
): { angenommen: File[]; abgelehnt: Abgelehnt[] } {
  const angenommen: File[] = [];
  const abgelehnt: Abgelehnt[] = [];

  let anzahl = vorhanden.length;
  let summe = vorhanden.reduce((s, p) => s + p.file.size, 0);

  for (const datei of neue) {
    if (anzahl >= MAX_FILES) {
      abgelehnt.push({ name: datei.name, grund: "zu_viele" });
      continue;
    }
    if (summe + datei.size > MAX_TOTAL_BYTES) {
      abgelehnt.push({ name: datei.name, grund: "zu_gross" });
      continue;
    }
    angenommen.push(datei);
    anzahl += 1;
    summe += datei.size;
  }

  return { angenommen, abgelehnt };
}

/** Meldung für abgelehnte Dateien. Leerer String, wenn nichts abgelehnt wurde. */
export function ablehnungsText(abgelehnt: readonly Abgelehnt[]): string {
  if (abgelehnt.length === 0) return "";
  const zuViele = abgelehnt.filter((a) => a.grund === "zu_viele").map((a) => a.name);
  const zuGross = abgelehnt.filter((a) => a.grund === "zu_gross").map((a) => a.name);
  const teile: string[] = [];
  if (zuViele.length > 0) {
    teile.push(`Höchstens ${MAX_FILES} Dateien je Nachricht — nicht übernommen: ${zuViele.join(", ")}`);
  }
  if (zuGross.length > 0) {
    teile.push(
      `Zusammen höchstens ${MAX_TOTAL_BYTES / 1024 / 1024} MB — nicht übernommen: ${zuGross.join(", ")}`,
    );
  }
  return teile.join("\n");
}

/**
 * Baut den Nachrichtentext: Begleittext zuerst, danach je Token eine Zeile.
 *
 * Reihenfolge ist die Auswahlreihenfolge — der Empfänger sieht die Anhänge so,
 * wie der Absender sie gewählt hat.
 */
export function nachrichtBauen(text: string, tokens: readonly string[]): string {
  const sauber = text.trim();
  if (tokens.length === 0) return sauber;
  const angehaengt = tokens.join("\n");
  return sauber ? `${sauber}\n${angehaengt}` : angehaengt;
}
